import {
	Button,
	Card,
	CardDescription,
	CardHeader,
	CardTitle,
	cn,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	Input,
	Skeleton,
} from "@monbits/ui";
import { AxiosError } from "axios";
import { Check, Eye, EyeOff } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { StatusBadge } from "src/components/status-badge";
import { TwoFactorBackupCodes } from "src/components/two-factor/two-factor-backup-codes";
import { TwoFactorSetup } from "src/components/two-factor/two-factor-setup";
import { TwoFactorVerification } from "src/components/two-factor/two-factor-verification";
import { useTwoFactor } from "src/hooks/use-two-factor";
import type { TwoFactorSetupResponse } from "src/providers/auth-provider";
import authProvider from "src/providers/auth-provider";
import { validatePassword as validatePasswordStrength } from "src/utils/password-validator";

type Step = "idle" | "setup" | "verify-disable" | "backup-codes";

interface PasswordFieldProps {
	label: string;
	placeholder: string;
	value: string;
	onChange: (value: string) => void;
	show: boolean;
	onToggleShow: () => void;
	error?: string;
	description?: string;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
	label,
	placeholder,
	value,
	onChange,
	show,
	onToggleShow,
	error,
	description,
}) => (
	<Field>
		<FieldLabel>{label}</FieldLabel>
		<div className="relative">
			<Input
				type={show ? "text" : "password"}
				placeholder={placeholder}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className={cn("pr-10", error && "border-destructive")}
			/>
			<button
				type="button"
				aria-label={show ? "Hide password" : "Show password"}
				onClick={onToggleShow}
				className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
			>
				{show ? (
					<EyeOff aria-hidden="true" className="h-4 w-4" />
				) : (
					<Eye aria-hidden="true" className="h-4 w-4" />
				)}
			</button>
		</div>
		{(error || description) && (
			<FieldDescription
				className={error ? "text-destructive" : "text-muted-foreground"}
			>
				{error || description}
			</FieldDescription>
		)}
	</Field>
);

const AccountSettingsPage: React.FC = () => {
	// Two-Factor Authentication state
	const {
		status,
		isLoading: is2FALoading,
		isSubmitting: is2FASubmitting,
		fetchStatus,
		setupTwoFactor,
		enableTwoFactor,
		disableTwoFactor,
	} = useTwoFactor();

	const [step, setStep] = React.useState<Step>("idle");
	const [setupData, setSetupData] =
		React.useState<TwoFactorSetupResponse | null>(null);
	const [backupCodes, setBackupCodes] = React.useState<string[]>([]);

	// Password change state
	const [isChangePasswordOpen, setIsChangePasswordOpen] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [currentPassword, setCurrentPassword] = React.useState("");
	const [newPassword, setNewPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");
	const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
	const [showNewPassword, setShowNewPassword] = React.useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
	const [currentPasswordError, setCurrentPasswordError] = React.useState("");
	const [newPasswordError, setNewPasswordError] = React.useState("");
	const [confirmPasswordError, setConfirmPasswordError] = React.useState("");

	React.useEffect(() => {
		fetchStatus();
	}, [fetchStatus]);

	// Password functions
	const resetPasswordForm = () => {
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
		setCurrentPasswordError("");
		setNewPasswordError("");
		setConfirmPasswordError("");
		setShowCurrentPassword(false);
		setShowNewPassword(false);
		setShowConfirmPassword(false);
	};

	const validateCurrentPassword = (value: string): boolean => {
		if (!value) {
			setCurrentPasswordError("Current password is required.");
			return false;
		}
		setCurrentPasswordError("");
		return true;
	};

	const validateNewPassword = (value: string): boolean => {
		if (!value) {
			setNewPasswordError("New password is required.");
			return false;
		}
		if (value === currentPassword) {
			setNewPasswordError("Pick a password different from your current one.");
			return false;
		}

		const result = validatePasswordStrength(value);
		if (!result.valid) {
			setNewPasswordError(result.errors[0]); // Show first error
			return false;
		}

		setNewPasswordError("");
		return true;
	};

	const validateConfirmPassword = (value: string): boolean => {
		if (!value) {
			setConfirmPasswordError("Confirm your new password.");
			return false;
		}
		if (value !== newPassword) {
			setConfirmPasswordError("Passwords don't match.");
			return false;
		}
		setConfirmPasswordError("");
		return true;
	};

	const handleCurrentPasswordChange = (value: string) => {
		setCurrentPassword(value);
		if (currentPasswordError) validateCurrentPassword(value);
	};

	const handleNewPasswordChange = (value: string) => {
		setNewPassword(value);
		if (newPasswordError) validateNewPassword(value);
		if (confirmPassword) validateConfirmPassword(confirmPassword);
	};

	const handleConfirmPasswordChange = (value: string) => {
		setConfirmPassword(value);
		if (confirmPasswordError) validateConfirmPassword(value);
	};

	const handleChangePassword = async (e: React.FormEvent) => {
		e.preventDefault();

		const isCurrentValid = validateCurrentPassword(currentPassword);
		const isNewValid = validateNewPassword(newPassword);
		const isConfirmValid = validateConfirmPassword(confirmPassword);

		if (!isCurrentValid || !isNewValid || !isConfirmValid) {
			return;
		}

		try {
			setIsSubmitting(true);
			await authProvider.changePassword(currentPassword, newPassword);

			toast.success("Password updated", {
				description: "Use it next time you sign in.",
				icon: <Check className="h-4 w-4" />,
			});

			setIsChangePasswordOpen(false);
			resetPasswordForm();
		} catch (error) {
			if (error instanceof AxiosError) {
				const message =
					error.response?.data?.message || "Couldn't change password.";
				if (
					message.includes("current password") ||
					message.includes("invalid")
				) {
					setCurrentPasswordError("That's not your current password.");
				} else {
					toast.error("Couldn't change password", { description: message });
				}
			} else {
				toast.error("Couldn't change password", {
					description: "Try again in a moment.",
				});
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	// Two-Factor functions
	const handleEnableClick = async () => {
		const data = await setupTwoFactor();
		if (data) {
			setSetupData(data);
			setStep("setup");
		}
	};

	const handleSetupVerify = async (code: string) => {
		if (!setupData) return false;
		const success = await enableTwoFactor(
			code,
			setupData.secret,
			setupData.backupCodes,
		);
		if (success) {
			setBackupCodes(setupData.backupCodes);
			setStep("backup-codes");
		}
		return success;
	};

	const handleDisableClick = () => {
		setStep("verify-disable");
	};

	const handleDisableVerify = async (code: string) => {
		const success = await disableTwoFactor(code);
		if (success) {
			setStep("idle");
		}
		return success;
	};

	const handleCloseDialog = () => {
		setStep("idle");
		setSetupData(null);
		setBackupCodes([]);
	};

	const is2FAEnabled = status?.enabled ?? false;
	const is2FAStatusUnknown = !status && is2FALoading;

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<h1 className="sr-only">Account settings</h1>

			<div className="flex items-center gap-3">
				<span aria-hidden className="h-5 w-[3px] rounded-full bg-primary" />
				<p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
					Account · Security and access
				</p>
			</div>

			<Card>
				<CardHeader>
					<div className="flex items-start justify-between gap-3">
						<div className="space-y-1">
							<CardTitle className="text-base font-medium">Password</CardTitle>
							<CardDescription className="text-xs">
								Change your password to keep your account secure.
							</CardDescription>
						</div>
						<Button
							variant="outline"
							size="sm"
							className="flex-shrink-0"
							onClick={() => setIsChangePasswordOpen(true)}
						>
							Change password
						</Button>
					</div>
				</CardHeader>
			</Card>

			<Card>
				<CardHeader>
					<div className="flex items-start justify-between gap-3">
						<div className="space-y-1">
							<div className="flex flex-wrap items-center gap-2">
								<CardTitle className="text-base font-medium">
									Two-factor authentication
								</CardTitle>
								{is2FAStatusUnknown ? (
									<Skeleton className="h-5 w-20 rounded-full" />
								) : is2FAEnabled ? (
									<StatusBadge type="success">Enabled</StatusBadge>
								) : (
									<StatusBadge type="warning">Recommended</StatusBadge>
								)}
							</div>
							{is2FAStatusUnknown ? (
								<Skeleton className="mt-1 h-3 w-72" />
							) : (
								<CardDescription className="text-xs">
									{is2FAEnabled
										? "Your account is protected with an authenticator app."
										: "Add an extra layer of security with a verification code from your phone."}
								</CardDescription>
							)}
						</div>
						<Button
							variant={is2FAEnabled ? "outline" : "default"}
							size="sm"
							className="flex-shrink-0"
							onClick={is2FAEnabled ? handleDisableClick : handleEnableClick}
							disabled={is2FALoading || is2FASubmitting}
						>
							{is2FAEnabled ? "Disable" : "Enable"}
						</Button>
					</div>
				</CardHeader>
			</Card>

			{/* Password Change Dialog */}
			<Dialog
				open={isChangePasswordOpen}
				onOpenChange={setIsChangePasswordOpen}
			>
				<DialogContent>
					<form onSubmit={handleChangePassword}>
						<DialogHeader>
							<DialogTitle>Change password</DialogTitle>
							<DialogDescription>
								Pick something at least 12 characters with a mix of letters,
								digits, and symbols.
							</DialogDescription>
						</DialogHeader>

						<FieldGroup className="py-4">
							<PasswordField
								label="Current password"
								placeholder="Your current password"
								value={currentPassword}
								onChange={handleCurrentPasswordChange}
								show={showCurrentPassword}
								onToggleShow={() =>
									setShowCurrentPassword(!showCurrentPassword)
								}
								error={currentPasswordError}
							/>

							<PasswordField
								label="New password"
								placeholder="At least 12 characters"
								value={newPassword}
								onChange={handleNewPasswordChange}
								show={showNewPassword}
								onToggleShow={() => setShowNewPassword(!showNewPassword)}
								error={newPasswordError}
								description="Mix of uppercase, lowercase, digit, and symbol."
							/>

							<PasswordField
								label="Confirm new password"
								placeholder="Re-enter the new password"
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
								show={showConfirmPassword}
								onToggleShow={() =>
									setShowConfirmPassword(!showConfirmPassword)
								}
								error={confirmPasswordError}
							/>
						</FieldGroup>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setIsChangePasswordOpen(false);
									resetPasswordForm();
								}}
								disabled={isSubmitting}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? (
									<span className="flex items-center gap-2">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
										Saving…
									</span>
								) : (
									"Change password"
								)}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Two-Factor Authentication Dialogs */}
			<Dialog open={step !== "idle"} onOpenChange={handleCloseDialog}>
				<DialogContent className="max-w-md">
					{step === "setup" && setupData && (
						<>
							<DialogHeader>
								<DialogTitle>Set up two-factor authentication</DialogTitle>
								<DialogDescription>
									Scan the QR code with your authenticator app.
								</DialogDescription>
							</DialogHeader>
							<TwoFactorSetup
								otpURL={setupData.otpUrl}
								secret={setupData.secret}
								onVerify={handleSetupVerify}
								onCancel={handleCloseDialog}
								isSubmitting={is2FASubmitting}
							/>
						</>
					)}

					{step === "verify-disable" && (
						<>
							<DialogHeader>
								<DialogTitle>Disable two-factor authentication</DialogTitle>
								<DialogDescription>
									Enter a code from your authenticator app to confirm.
								</DialogDescription>
							</DialogHeader>
							<TwoFactorVerification
								onVerify={handleDisableVerify}
								onCancel={handleCloseDialog}
								isSubmitting={is2FASubmitting}
								showBackupOption={true}
								description="Enter the code from your authenticator app."
								submitLabel="Disable 2FA"
							/>
						</>
					)}

					{step === "backup-codes" && backupCodes.length > 0 && (
						<>
							<DialogHeader>
								<DialogTitle>Save your backup codes</DialogTitle>
								<DialogDescription>
									Use one of these if you lose access to your authenticator app.
									We won't show them again.
								</DialogDescription>
							</DialogHeader>
							<TwoFactorBackupCodes
								codes={backupCodes}
								onClose={handleCloseDialog}
								showWarning={true}
							/>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default AccountSettingsPage;
